import { ContentService } from '@blk/cwp-ng-framework';
import { OutputDesignation, PortfolioType, RiskInsightsDefaultDCR } from '@ddt-web/config';
import { NumberUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';

import { CellData } from '../cell-data';
import { ComponentData } from '../component-data';
import { CellRow } from '../interfaces';
import { TabData } from '../tab-data';

export abstract class InsightsTransform {
	protected readonly FLOOR_VALUE: number = 1;
	protected readonly CEIL_VALUE: number = 2.25;
	private _filteredInsightsPortfolioData: CellRow[] = [];

	public benchmarkIndex: number;
	public insightsTab: TabData;
	public factorsTab: TabData;

	constructor(public contentSvc: ContentService) { }

	public transform(componentData: ComponentData): any {
		if (componentData && componentData.success) {
			this.insightsTab = componentData.tabs[0];
			this.factorsTab = componentData.tabs[1];
			this.setupContent();
			const options = this.prepareData(componentData);
			return this.evaluateInsights(options);
		} else {
			return undefined;
		}
	}

	public prepareData(componentData: ComponentData): Array<CellRow> {
		if (this.insightsTab && this.insightsTab.tableData[0]) {
			this.reconcilePortfolioData(componentData);
			this.benchmarkIndex = this._filteredInsightsPortfolioData.findIndex(
				each => get(each, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK || get(each, 'field.isBenchmark.rawValue') === true
			);
			return this._filteredInsightsPortfolioData.map(each => {
				return get(each, 'field.portfolioType.rawValue') !== PortfolioType.MODEL
					? each
					: null;
			});
		} else {
			return [];
		}
	}

	public evaluateInsights(options: Array<CellRow>): Array<CellRow> {
		const dropdownOptions = [];
		options.forEach((cell, index) => {
			if (cell && !this.insightsTab.error && !this.factorsTab.error) {
				const overviewParagraph = this.getEvaluatedOverview(index);
				const conclusionParagraph = this.getEvaluatedConclusion(index);
				if (overviewParagraph && conclusionParagraph) {
					cell.field['overviewParagraph'] = new CellData(null, overviewParagraph, overviewParagraph);
					cell.field['conclusionParagraph'] = new CellData(null, conclusionParagraph, conclusionParagraph);
					dropdownOptions.push(cell);
				}
			}
		});
		return dropdownOptions;
	}

	protected abstract setupContent(): void;
	protected abstract getEvaluatedOverview(index: number): string;
	protected abstract getEvaluatedConclusion(index: number): string;

	public getRisk(value: number): string {
		return value > this.CEIL_VALUE
			? this.translate('RISK_INSIGHTS_RISK_LEVEL_1', 'high', this.contentSvc)
			: value < this.FLOOR_VALUE
				? this.translate('RISK_INSIGHTS_RISK_LEVEL_3', 'low', this.contentSvc)
				: this.translate('RISK_INSIGHTS_RISK_LEVEL_2', 'medium', this.contentSvc);
	}

	public getBenchmark(value: number): string {
		return value > this.CEIL_VALUE
			? this.translate('RISK_INSIGHTS_BENCHMARK_LEVEL_1', 'significantly', this.contentSvc)
			: value < this.FLOOR_VALUE
				? this.translate('RISK_INSIGHTS_BENCHMARK_LEVEL_3', 'slightly', this.contentSvc)
				: this.translate('RISK_INSIGHTS_BENCHMARK_LEVEL_2', 'moderately', this.contentSvc);
	}

	public getCompareNumberDescription(value: number, target: number, withinRatio: number): string {
		const range = Math.abs(withinRatio / 100 * target);
		return value > target + range
			? this.translate('RISK_INSIGHTS_RANGE_1', 'higher', this.contentSvc)
			: value < target - range ? this.translate('RISK_INSIGHTS_RANGE_3', 'lower', this.contentSvc) : this.translate('RISK_INSIGHTS_RANGE_2', 'similar', this.contentSvc);
	}

	public getCompareDurationDescription(value: number, target: number, withinRatio: number): string {
		const range = Math.abs(withinRatio / 100 * target);
		return value > target + range
			? this.translate('RISK_INSIGHTS_DURATION_1', 'longer', this.contentSvc)
			: value < target - range
				? this.translate('RISK_INSIGHTS_DURATION_3', 'shorter', this.contentSvc)
				: this.translate('RISK_INSIGHTS_DURATION_2', 'similar', this.contentSvc);
	}

	public getDiffPercentageFromBenchmark(value, target): string {
		return NumberUtil.formatNumber((value / target - 1) * 100);
	}

	/**
	 * reconcilePortfolioData: using the tab level portfolio data we create a reconciled version of the component level portfolio data
	 * @param {ComponentData} componentData the component data object
	 */
	private reconcilePortfolioData(componentData: ComponentData): void {
		const tabLevelInsightsPortfolioData = get(componentData, 'tabs[0].tableData[0].portfolioData');
		tabLevelInsightsPortfolioData.forEach((tabPortfolio, index) => {
			if (tabPortfolio !== null) {
				this._filteredInsightsPortfolioData.push(componentData.portfolioData[index]);
			}
		});
	}

	protected translate(key: string, defaultValue: string, contentSvc: ContentService): string {
		let codeDefaultValue = defaultValue;
		if (RiskInsightsDefaultDCR.hasOwnProperty(key)) {
			codeDefaultValue = RiskInsightsDefaultDCR[key];
		}
		return contentSvc.get(key, codeDefaultValue);
	}
}
